import {
  Ban,
  Bell,
  Brain,
  Building,
  Calendar,
  Clock,
  CreditCard,
  Gift,
  Globe,
  History,
  Layout,
  Palette,
  Settings,
  Star,
  User,
  UserCheck,
  Users,
} from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { env } from "@/lib/env";
import { useAuthStore } from "@/lib/store/auth-enhanced";

export interface SettingsItem {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  category: string;
  priority: "high" | "medium" | "low";
  isInline?: boolean; // For mobile language/appearance
  path?: string; // Navigation path
  /**
   * When true, the item only appears in the mobile settings page. Used for
   * shortcuts to top-level features (e.g. Customers list) that have their
   * own nav tab on desktop but lost that tab on mobile when the bar was
   * reshuffled. Desktop consumers filter these out via `useSettingsSections`.
   */
  mobileOnly?: boolean;
}

export interface SettingsCategory {
  title: string;
  description: string;
}

export const useSettingsItems = (): SettingsItem[] => {
  const { t } = useTranslation("settings");
  const { user } = useAuthStore();
  const isAdmin = user?.role.some((r) => r === "ADMIN" || r === "SUPER_ADMIN") ?? false;

  return useMemo(
    () => [
      // Salon — profile & configuration
      {
        id: "business",
        title: t("sections.business.title"),
        icon: Building,
        description: t("sections.business.description"),
        category: "salon",
        priority: "high",
        path: "/settings/business",
      },
      {
        id: "salon-config",
        title: t("sections.salonConfig.title"),
        icon: Settings,
        description: t("sections.salonConfig.description"),
        category: "salon",
        priority: "high",
        path: "/settings/salon-config",
      },
      ...(isAdmin
        ? [
            {
              id: "audit-log",
              title: t("sections.auditLog.title"),
              icon: History,
              description: t("sections.auditLog.description"),
              category: "salon",
              priority: "low" as const,
              path: "/settings/audit-log",
            } as SettingsItem,
          ]
        : []),

      // Booking & Operations — staff, schedule, and booking rules
      {
        id: "workers",
        title: t("sections.teamManagement.title"),
        icon: UserCheck,
        description: t("sections.teamManagement.description"),
        category: "booking-ops",
        priority: "high",
        path: "/settings/workers",
      },
      ...(env.flags.showExperimental
        ? [
            {
              id: "shift-management",
              title: t("sections.shiftManagement.title"),
              icon: Clock,
              description: t("sections.shiftManagement.description"),
              category: "booking-ops",
              priority: "medium" as const,
              path: "/settings/shift-management",
            } as SettingsItem,
          ]
        : []),
      {
        id: "holidays",
        title: t("sections.holidays.title"),
        icon: Calendar,
        description: t("sections.holidays.description"),
        category: "booking-ops",
        priority: "medium",
        path: "/settings/holidays",
      },
      {
        id: "booking-behavior",
        title: t("sections.bookingBehavior.title"),
        icon: Brain,
        description: t("sections.bookingBehavior.description"),
        category: "booking-ops",
        priority: "medium",
        path: "/settings/booking-behavior",
      },
      {
        id: "limits",
        title: t("sections.limits.title"),
        icon: Ban,
        description: t("sections.limits.description"),
        category: "booking-ops",
        priority: "high",
        path: "/settings/limits",
      },
      ...(env.flags.showExperimental
        ? [
            {
              id: "notifications",
              title: t("sections.notifications.title"),
              icon: Bell,
              description: t("sections.notifications.description"),
              category: "booking-ops",
              priority: "medium" as const,
              path: "/settings/notifications",
            } as SettingsItem,
          ]
        : []),

      // Storefront — what customers see
      {
        id: "web-page",
        title: t("sections.webPage.title"),
        icon: Layout,
        description: t("sections.webPage.description"),
        category: "storefront",
        priority: "high",
        path: "/settings/web-page",
      },
      {
        id: "online",
        title: t("sections.online.title"),
        icon: Globe,
        description: t("sections.online.description"),
        category: "storefront",
        priority: "medium",
        path: "/settings/online",
      },

      // Integrations — external service connections
      {
        id: "google-business",
        title: t("sections.googleBusiness.title"),
        icon: Star,
        description: t("sections.googleBusiness.description"),
        category: "integrations",
        priority: "medium",
        path: "/settings/google-business",
      },

      // Tools — mobile-only shortcuts to top-level features whose
      // dedicated tab was removed from the mobile bar. The Customers tab
      // got replaced by Customer Service when the mobile layout was
      // reshuffled, so this is how mobile users still reach the customer
      // list. `path` points outside `/settings/...` so the mobile click
      // handler navigates straight to `/customers`.
      {
        id: "customers-shortcut",
        title: t("sections.customersShortcut.title"),
        icon: Users,
        description: t("sections.customersShortcut.description"),
        category: "tools",
        priority: "high",
        path: "/customers",
        mobileOnly: true,
      },

      // Add-ons
      {
        id: "subscriptions",
        title: t("sections.subscriptions.title"),
        icon: CreditCard,
        description: t("sections.subscriptions.description"),
        category: "addons",
        priority: "high",
        path: "/settings/subscriptions",
      },
      ...(env.flags.showExperimental
        ? [
            {
              id: "rewards",
              title: t("sections.rewards.title"),
              icon: Gift,
              description: t("sections.rewards.description"),
              category: "addons",
              priority: "medium" as const,
              path: "/settings/rewards",
            } as SettingsItem,
          ]
        : []),

      // Personal
      {
        id: "profile",
        title: t("sections.profile.title"),
        icon: User,
        description: t("sections.profile.description"),
        category: "personal",
        priority: "high",
        path: "/settings/profile",
      },
      {
        id: "appearance",
        title: t("sections.appearance.title"),
        icon: Palette,
        description: t("sections.appearance.description"),
        category: "personal",
        priority: "high",
        path: "/settings/appearance",
      },
    ],
    [t, isAdmin]
  );
};

export const useSettingsCategories = (): Record<string, SettingsCategory> => {
  const { t } = useTranslation("settings");

  return useMemo(
    () => ({
      salon: {
        title: t("categories.salon.title"),
        description: t("categories.salon.description"),
      },
      "booking-ops": {
        title: t("categories.booking-ops.title"),
        description: t("categories.booking-ops.description"),
      },
      storefront: {
        title: t("categories.storefront.title"),
        description: t("categories.storefront.description"),
      },
      integrations: {
        title: t("categories.integrations.title"),
        description: t("categories.integrations.description"),
      },
      tools: {
        title: t("categories.tools.title"),
        description: t("categories.tools.description"),
      },
      addons: {
        title: t("categories.addons.title"),
        description: t("categories.addons.description"),
      },
      personal: {
        title: t("categories.personal.title"),
        description: t("categories.personal.description"),
      },
    }),
    [t]
  );
};

export const groupSettingsByCategory = (
  settings: SettingsItem[]
): Record<string, SettingsItem[]> => {
  return settings.reduce(
    (acc, setting) => {
      if (!acc[setting.category]) {
        acc[setting.category] = [];
      }
      acc[setting.category].push(setting);
      return acc;
    },
    {} as Record<string, SettingsItem[]>
  );
};

export const getSettingsItemsByPriority = (
  settings: SettingsItem[],
  priority: "high" | "medium" | "low"
): SettingsItem[] => {
  return settings.filter((setting) => setting.priority === priority);
};

export const getInlineSettings = (settings: SettingsItem[]): SettingsItem[] => {
  return settings.filter((setting) => setting.isInline);
};

export const getNavigationSettings = (settings: SettingsItem[]): SettingsItem[] => {
  return settings.filter((setting) => !setting.isInline);
};

// Static settings items for non-hook usage (like validation)
export const getStaticSettingsItems = (): SettingsItem[] => {
  return [
    // Salon
    {
      id: "business",
      title: "Business",
      icon: Building,
      description: "Business details",
      category: "salon",
      priority: "high" as const,
      path: "/settings/business",
    },
    {
      id: "salon-config",
      title: "Salon Config",
      icon: Settings,
      description: "Salon configuration",
      category: "salon",
      priority: "high" as const,
      path: "/settings/salon-config",
    },
    {
      id: "audit-log",
      title: "Settings Change Log",
      icon: History,
      description: "Track configuration changes",
      category: "salon",
      priority: "low" as const,
      path: "/settings/audit-log",
    },

    // Booking & Operations
    {
      id: "workers",
      title: "Team Management",
      icon: UserCheck,
      description: "Manage staff",
      category: "booking-ops",
      priority: "high" as const,
      path: "/settings/workers",
    },
    {
      id: "shift-management",
      title: "Shift Management",
      icon: Clock,
      description: "Shift schedules",
      category: "booking-ops",
      priority: "medium" as const,
      path: "/settings/shift-management",
    },
    {
      id: "holidays",
      title: "Holidays",
      icon: Calendar,
      description: "Business holidays",
      category: "booking-ops",
      priority: "medium" as const,
      path: "/settings/holidays",
    },
    {
      id: "booking-behavior",
      title: "Booking Behavior",
      icon: Brain,
      description: "Booking rules",
      category: "booking-ops",
      priority: "medium" as const,
      path: "/settings/booking-behavior",
    },
    {
      id: "limits",
      title: "Limits",
      icon: Ban,
      description: "Booking limits",
      category: "booking-ops",
      priority: "high" as const,
      path: "/settings/limits",
    },
    {
      id: "notifications",
      title: "Notifications",
      icon: Bell,
      description: "Email reminders",
      category: "booking-ops",
      priority: "medium" as const,
      path: "/settings/notifications",
    },

    // Storefront
    {
      id: "web-page",
      title: "Web Page",
      icon: Layout,
      description: "Hero banner and announcement for your public booking page",
      category: "storefront",
      priority: "high" as const,
      path: "/settings/web-page",
    },
    {
      id: "online",
      title: "Online Booking",
      icon: Globe,
      description: "Storefront settings",
      category: "storefront",
      priority: "medium" as const,
      path: "/settings/online",
    },

    // Integrations
    {
      id: "google-business",
      title: "Google Business",
      icon: Star,
      description: "Connect your Google Business Profile to sync hours, services, and reviews",
      category: "integrations",
      priority: "medium" as const,
      path: "/settings/google-business",
    },

    // Add-ons
    {
      id: "subscriptions",
      title: "Subscriptions",
      icon: CreditCard,
      description: "Manage salon subscription",
      category: "addons",
      priority: "high" as const,
      path: "/settings/subscriptions",
    },
    {
      id: "rewards",
      title: "Rewards",
      icon: Gift,
      description: "Rewards program",
      category: "addons",
      priority: "medium" as const,
      path: "/settings/rewards",
    },

    // Personal
    {
      id: "profile",
      title: "Profile",
      icon: User,
      description: "Personal information",
      category: "personal",
      priority: "high" as const,
      path: "/settings/profile",
    },
    {
      id: "appearance",
      title: "Appearance",
      icon: Palette,
      description: "Visual theme",
      category: "personal",
      priority: "high" as const,
      path: "/settings/appearance",
    },
  ];
};
